import { getEnvVar } from '../../utils/env';

type CouponValidationProps = {
  couponCode: string;
  productPaymentIds: number[];
  jwt?: string;
};

type CouponValidationResponse = {
  valid: boolean;
  productPayment: {
    discountedInitPrice: number;
  };
  productPaymentId: number;
};

export type CouponValidationResult = {
  valid: boolean;
  discountedInitPrice?: number;
  productPaymentId?: number;
};

const INVALID_RESULT: CouponValidationResult = { valid: false };

const buildVoucherUrl = (
  env: string,
  couponCode: string,
  productPaymentId: number,
): string =>
  `https://voucher-end-user.${env}.vmnd.tv/validate/${couponCode}?productPaymentId=${productPaymentId}`;

const createHeaders = (jwt?: string): HeadersInit =>
  jwt ? { Authorization: `Bearer ${jwt}` } : {};

const validateSingleCoupon = async (
  url: string,
  headers: HeadersInit,
  productPaymentId: number,
): Promise<CouponValidationResult> => {
  try {
    const response = await fetch(url, { method: 'GET', headers });

    if (!response.ok) {
      return INVALID_RESULT;
    }

    const data: CouponValidationResponse = await response.json();

    if (!data?.valid) {
      return INVALID_RESULT;
    }

    return {
      valid: true,
      discountedInitPrice: data.productPayment?.discountedInitPrice,
      productPaymentId: data.productPaymentId ?? productPaymentId,
    };
  } catch {
    return INVALID_RESULT;
  }
};

/**
 * Validates a coupon code against multiple product payment IDs
 * Returns validation results for each product payment in parallel
 */
export const validateCoupon = async ({
  couponCode,
  productPaymentIds,
  jwt,
}: CouponValidationProps): Promise<CouponValidationResult[]> => {
  const validPaymentIds = productPaymentIds?.filter(Boolean) ?? [];
  if (validPaymentIds.length === 0) {
    return [];
  }

  const env = getEnvVar('NEXT_PUBLIC_ENVIRONMENT') ?? '';
  const headers = createHeaders(jwt);

  const validations = validPaymentIds.map((productPaymentId) => {
    const url = buildVoucherUrl(env, couponCode, productPaymentId);
    return validateSingleCoupon(url, headers, productPaymentId);
  });

  return Promise.all(validations);
};
